import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { toast, ToastContainer } from "react-toastify";

import { addnewAppointment } from "../../redux/slices/appointmentsSlice";
import { saveAppointmentsToDb } from "../../redux/apis/saveAppointmentsToDb";
import { addNotification } from "../../redux/slices/notificationSlice";

import { getAvailabilityByDate } from "../../redux/apis/Availability/doctorAvailabilitySlice";

import { getProfileImageUrl } from "../utils/imageUtils";
import { getBusinessUserProfileImageUrl } from "../utils/buserimageUtils";

const BookAppointmentPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const authUser = useSelector((state) => state.auth?.user);
  const businessUser = useSelector((state) => state.selectedBusiness?.user);

  const {
    sessions = [],
    loading,
    message,
  } = useSelector((state) => state.doctorAvailability);

  const [appointmentDate, setAppointmentDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const BASE_URL =
    process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:5000/api";

  const DEFAULT_IMAGE = `${BASE_URL}/uploads/public/default.png`;

  // ============================================================
  // AUTH CHECK
  // ============================================================
  useEffect(() => {
    if (!authUser || authUser.role !== "user") {
      navigate("/login");
    }
  }, [authUser, navigate]);

  // ============================================================
  // ID HELPER
  // ============================================================
  const getIdString = (id) => {
    if (!id) return null;

    if (typeof id === "string") {
      return id;
    }

    return id?.$oid || id?.toString?.() || null;
  };

  // ============================================================
  // NAME HELPER
  // ============================================================
  const getDisplayName = (user) =>
    user?.fullName || user?.username || user?.name || "N/A";

  // ============================================================
  // TIME CHECK
  // ============================================================
  const isSlotInPast = (date, startTime) => {
    const now = new Date();
    const slotDateTime = new Date(`${date} ${startTime}`);

    return slotDateTime < now;
  };

  // ============================================================
  // FETCH DOCTOR SLOTS
  // ============================================================
  const fetchSlots = (date) => {
    const doctorId = getIdString(businessUser?._id);

    if (!date || !doctorId) {
      console.log("⚠️ Cannot fetch slots");
      console.log("Date:", date);
      console.log("Doctor ID:", doctorId);
      return;
    }

    console.log("======================================");
    console.log("📅 FETCHING DOCTOR AVAILABILITY");
    console.log("Doctor ID:", doctorId);
    console.log("Date:", date);
    console.log("======================================");

    dispatch(
      getAvailabilityByDate({
        doctorId,
        date,
      }),
    );
  };

  // ============================================================
  // DATE CHANGE
  // ============================================================
  useEffect(() => {
    if (appointmentDate) {
      setSelectedSlot(null);
      fetchSlots(appointmentDate);
    }
  }, [appointmentDate, businessUser?._id]);

  // ============================================================
  // AUTO SELECT FIRST VALID SLOT
  // ============================================================
  useEffect(() => {
    if (!appointmentDate) return;

    const firstSlot = sessions
      .flatMap((s) => s.slots || [])
      .find(
        (slot) =>
          slot &&
          slot.isActive &&
          !slot.isBooked &&
          !isSlotInPast(appointmentDate, slot.startTime),
      );

    if (firstSlot) {
      setSelectedSlot(firstSlot);

      console.log("======================================");
      console.log("⏰ AUTO SELECTED SLOT");
      console.log("Slot ID:", firstSlot.slotId);
      console.log("Start:", firstSlot.startTime);
      console.log("End:", firstSlot.endTime);
      console.log("======================================");
    }
  }, [sessions, appointmentDate]);

  // ============================================================
  // BOOK APPOINTMENT
  // ============================================================
  const handleBooking = async () => {
    if (!appointmentDate || !selectedSlot) {
      toast.error("Please select date and slot.");
      return;
    }

    if (!businessUser?._id) {
      toast.error("Doctor information is missing.");
      return;
    }

    if (!authUser?._id) {
      toast.error("Patient information is missing.");
      return;
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // ==========================================================
    // PATIENT INFO
    // ==========================================================
    const patientInfo = {
      id: getIdString(authUser?._id),
      name: getDisplayName(authUser),
      email: authUser?.email || "",
      phone: authUser?.phone || authUser?.contact || "",
    };

    // ==========================================================
    // DOCTOR ID
    // ==========================================================
    const doctorId = getIdString(businessUser?._id);

    // ==========================================================
    // DOCTOR INFO
    // ==========================================================
    const doctorInfo = {
      id: doctorId,
      name: getDisplayName(businessUser),
      username: businessUser?.username || "NO_USERNAME",
      email: businessUser?.email || "",
      phone: businessUser?.phone || "",
      qualityScore: businessUser?.qualityScore || 0,
    };

    // ==========================================================
    // FINAL APPOINTMENT DATA
    // ==========================================================
    const appointmentData = {
      appointmentId: uuidv4(),

      date: appointmentDate,

      time: selectedSlot.startTime,

      slotId: selectedSlot.slotId,

      timeZone,

      description,

      // PATIENT
      patientInfo,

      // DOCTOR
      doctorInfo,
    };

    setIsSubmitting(true);

    try {
      const response = await dispatch(
        saveAppointmentsToDb(appointmentData),
      ).unwrap();

      // ========================================================
      // SUCCESS RESPONSE DEBUG
      // ========================================================
      console.log("======================================");
      console.log("✅ APPOINTMENT SAVED SUCCESSFULLY");
      console.log("Backend Response:", response);
      console.log("Saved Appointment:", response?.appointment);
      console.log("Saved Patient Info:", response?.appointment?.patientInfo);
      console.log("Saved Doctor Info:", response?.appointment?.doctorInfo);
      console.log("======================================");

      // ========================================================
      // UPDATE REDUX
      // ========================================================
      dispatch(addnewAppointment(response.appointment));

      // ========================================================
      // REFRESH AVAILABILITY
      // ========================================================
      dispatch(
        getAvailabilityByDate({
          doctorId,
          date: appointmentDate,
        }),
      );

      // ========================================================
      // NOTIFICATION
      // ========================================================
      dispatch(
        addNotification({
          message: "Appointment booked successfully!",
          role: "patient",
        }),
      );

      toast.success("Appointment booked successfully!");

      navigate("/");
    } catch (err) {
      console.error("======================================");
      console.error("❌ BOOKING ERROR");
      console.error("Error:", err);
      console.error("======================================");

      toast.error(err || "Booking failed");

      // ========================================================
      // REFRESH SLOTS AFTER FAILURE
      // ========================================================
      dispatch(
        getAvailabilityByDate({
          doctorId,
          date: appointmentDate,
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // DATE LIMITS
  // ============================================================
  const minDate = new Date().toISOString().split("T")[0];

  const maxDate = new Date(Date.now() + 7 * 86400000)
    .toISOString()
    .split("T")[0];

  const allSlots = sessions.flatMap((s) => s.slots || []);

  // ============================================================
  // UI
  // ============================================================
  return (
    <div className="max-w-5xl mx-auto p-4 bg-gray-100 rounded-lg shadow-md">
      <ToastContainer />

      {/* BACK */}
      <button
        onClick={() => navigate(-1)}
        className="text-blue-600 mb-4 hover:underline"
      >
        ← Back
      </button>

      {/* ========================================================
          USER + DOCTOR
      ======================================================== */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        {[authUser, businessUser].map((user, index) => {
          const imageUrl =
            index === 0
              ? getProfileImageUrl(user?.profilePic)
              : getBusinessUserProfileImageUrl(user?.profilePic);

          const isDoctor = index === 1;

          return (
            <div key={index} className="flex-1 p-4 bg-white rounded shadow">
              <h3 className="font-bold mb-2">{isDoctor ? "Doctor" : "User"}</h3>

              {/* PROFILE IMAGE */}
              <img
                src={imageUrl || DEFAULT_IMAGE}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = DEFAULT_IMAGE;
                }}
                className="w-16 h-16 rounded-full mb-2 object-cover"
                alt="profile"
              />

              {/* NAME */}
              <p>
                <b>Name:</b> {getDisplayName(user)}
              </p>

              {/* USERNAME */}
              {isDoctor && (
                <p>
                  <b>Username:</b> {user?.username || "N/A"}
                </p>
              )}

              {/* EMAIL */}
              <p>
                <b>Email:</b> {user?.email || "N/A"}
              </p>

              {/* PHONE */}
              <p>
                <b>Phone:</b> {user?.phone || "N/A"}
              </p>

              {/* LOCATION */}
              <p>
                <b>Location:</b>{" "}
                {user?.city || user?.area || user?.state
                  ? `${user?.city || ""}${
                      user?.city && user?.area ? ", " : ""
                    }${user?.area || ""}${
                      (user?.city || user?.area) && user?.state ? ", " : ""
                    }${user?.state || ""}`
                  : "N/A"}
              </p>

              {/* ==================================================
                  GOOGLE MAPS LINK
              ================================================== */}
              {isDoctor && user?.googleMapLink && (
                <div className="mt-3">
                  <a
                    href={user.googleMapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                  >
                    📍 Open Google Maps
                  </a>
                </div>
              )}

              {/* NO MAP LINK */}
              {isDoctor && !user?.googleMapLink && (
                <p className="text-xs text-gray-500 mt-2">
                  📍 Google Maps location not available
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* ========================================================
          APPOINTMENT FORM
      ======================================================== */}
      <div className="bg-white p-4 rounded shadow">
        <h3 className="text-lg font-semibold mb-3">Appointment Details</h3>

        {/* DATE */}
        <input
          type="date"
          min={minDate}
          max={maxDate}
          value={appointmentDate}
          onChange={(e) => setAppointmentDate(e.target.value)}
          className="w-full p-2 border mb-4 rounded"
        />

        {/* ======================================================
            TIME SLOTS
        ====================================================== */}
        <div className="mb-4">
          <label className="font-semibold">Select Time Slot</label>

          {/* BEFORE DATE */}
          {!appointmentDate && (
            <p className="text-gray-500 mt-2">
              Please select a date to view available slots
            </p>
          )}

          {/* AFTER DATE */}
          {appointmentDate && (
            <>
              {loading && (
                <p className="mt-2 text-blue-600">Loading slots...</p>
              )}

              {!loading && allSlots.length === 0 && (
                <p className="text-red-500 mt-2">
                  {message || "No slots available"}
                </p>
              )}

              <div className="mt-2">
                {sessions.map((session) => (
                  <div key={session.name} className="mb-4">
                    <h4 className="font-semibold text-gray-700 mb-2">
                      {session.name}
                    </h4>

                    <div className="flex flex-wrap gap-2">
                      {session.slots
                        ?.filter(
                          (slot) =>
                            slot.isActive &&
                            !slot.isBooked &&
                            !isSlotInPast(appointmentDate, slot.startTime),
                        )
                        .map((slot) => {
                          const isSelected =
                            selectedSlot?.slotId === slot.slotId;

                          return (
                            <button
                              key={slot.slotId}
                              onClick={() => setSelectedSlot(slot)}
                              className={`px-3 py-2 border rounded ${
                                isSelected
                                  ? "bg-blue-500 text-white"
                                  : "bg-white hover:bg-blue-100"
                              }`}
                            >
                              {slot.startTime} - {slot.endTime}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* DESCRIPTION */}
        <textarea
          placeholder="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full p-2 border mb-4 rounded"
        />

        {/* BOOK BUTTON */}
        <button
          onClick={handleBooking}
          disabled={isSubmitting || !selectedSlot}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
        >
          {isSubmitting ? "Booking..." : "Book Appointment"}
        </button>
      </div>
    </div>
  );
};

export default BookAppointmentPage;
